import { zodResolver } from "@hookform/resolvers/zod";
import { feeFormSchema } from "features/fees/FeeSchemas";
import FeeFormSectionBasics from "features/fees/form/FeeFormSectionBasics";
import FeeFormSectionCalculation from "features/fees/form/FeeFormSectionCalculation";
import FeeFormSectionCatchUpInterest from "features/fees/form/FeeFormSectionCatchUpInterest";
import FeeFormSectionInitialOverrides from "features/fees/form/FeeFormSectionInitialOverrides";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useNamedDateListSuspense } from "gen/endpoints/named-dates/named-dates";
import { FeeCreateAPIDataRequest } from "gen/model/feeCreateAPIDataRequest";
import { produce } from "immer";
import { cloneDeep } from "lodash-es";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { ContentBlockGroup } from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormUnsavedIndicator } from "shared/components/form/FormUnsavedIndicator";

export interface FeeFormProps {
  fundFamilyId: string;
  feeData: FeeCreateAPIDataRequest;
  isOverride?: boolean;
  submitButtonText: string;
  successMessage?: string;
  onSubmit: (values: FeeCreateAPIDataRequest) => Promise<void>;
  onSuccess: () => void;
  onCancel: () => void;
}

const FeeForm: React.FC<FeeFormProps> = ({
  fundFamilyId,
  feeData: feeDataProp,
  isOverride,
  submitButtonText,
  successMessage,
  onSubmit,
  onSuccess,
  onCancel,
}) => {
  const { data: namedDateData } = useNamedDateListSuspense(fundFamilyId);

  const { data: fundEntitiesData } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
  });

  const feeData = useMemo(
    () =>
      produce(feeDataProp, (draft) => {
        for (const calc of draft.fee_calculations) {
          if (calc.custom_start_date && !calc.named_start_date_id) {
            calc.named_start_date_id = "custom";
          }
          if (calc.custom_end_date && !calc.named_end_date_id) {
            calc.named_end_date_id = "custom";
          }
        }
        return draft;
      }),
    [feeDataProp],
  );

  const form = useForm<
    FeeCreateAPIDataRequest & {
      fee_eligible_for_true_up: boolean;
    }
  >({
    resolver: zodResolver(feeFormSchema),
    defaultValues: {
      ...feeData,
      fee_eligible_for_true_up: !!feeData.fee.true_up_methodology,
    },
  });

  const onSubmitWrapper = (values: FeeCreateAPIDataRequest) => {
    // cloneDeep is needed because immer freezes the input data which
    // makes the form immutable.
    const fixedValues = produce(cloneDeep(values), (draft) => {
      for (const calc of draft.fee_calculations) {
        if (calc.named_start_date_id === "custom") {
          calc.named_start_date_id = null;
        }
        if (calc.named_end_date_id === "custom") {
          calc.named_end_date_id = null;
        }
      }
    });
    return onSubmit(fixedValues);
  };

  return (
    <Form
      form={form}
      onSubmit={onSubmitWrapper}
      onSuccess={onSuccess}
      successMessage={successMessage}
    >
      <ContentBlockGroup defaultIndex={[0, 1]}>
        <FeeFormSectionBasics
          control={form.control}
          fundFamilyId={fundFamilyId}
          fundEntitiesData={fundEntitiesData}
        />
        <FeeFormSectionCalculation
          control={form.control}
          namedDateData={namedDateData}
          isOverride={isOverride}
        />
        <FeeFormSectionInitialOverrides
          control={form.control}
          isDisabled={isOverride}
        />
        <FeeFormSectionCatchUpInterest
          control={form.control}
          isDisabled={isOverride}
        />
      </ContentBlockGroup>
      <FormFooter>
        <FormUnsavedIndicator />
        <FormFooterActions>
          <FormCancelButton onClick={onCancel} />
          <FormSubmitButton>{submitButtonText}</FormSubmitButton>
        </FormFooterActions>
      </FormFooter>
    </Form>
  );
};

export default FeeForm;
